// DPoP proofs (RFC 9449, ES256 compact JWS) for embed requests, so a copied session bearer is
// useless without this browser's non-extractable key. Pure: no storage, window or network.

// Must match hodor's dpop.Header.
export const DPOP_HEADER = 'DPoP'

// P-256 only; hodor pins the same curve.
export const DPOP_KEY_ALGORITHM: EcKeyGenParams = {
  name: 'ECDSA',
  namedCurve: 'P-256',
}

const SIGN_ALGORITHM: EcdsaParams = { name: 'ECDSA', hash: { name: 'SHA-256' } }

const base64Url = (bytes: ArrayBuffer | Uint8Array): string => {
  const view = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes)
  let binary = ''
  for (const byte of view) {
    binary += String.fromCharCode(byte)
  }
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

// A real ArrayBuffer (sliced), which WebCrypto's BufferSource type requires under TS 5.7+.
const utf8 = (value: string): ArrayBuffer => {
  const bytes = new TextEncoder().encode(value)
  return bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength) as ArrayBuffer
}

const encodeJson = (value: unknown): string => base64Url(utf8(JSON.stringify(value)))

// `ath` binds a proof to one session bearer; htm/htu alone would let it authorise another.
export const accessTokenHash = async (bearer: string): Promise<string> =>
  base64Url(await crypto.subtle.digest('SHA-256', utf8(bearer)))

// Only the four RFC 7638 members (the public half is exportable even though the private is not).
export const publicJwk = async (publicKey: CryptoKey): Promise<Record<string, string>> => {
  const jwk = await crypto.subtle.exportKey('jwk', publicKey)
  if (!jwk.x || !jwk.y) {
    throw new Error('embed dpop: exported JWK has no coordinates')
  }
  return { crv: 'P-256', kty: 'EC', x: jwk.x, y: jwk.y }
}

// RFC 7638 thumbprint; must match hodor's ecJWK.thumbprint() byte for byte or every exchange
// 400s. The literal's key order is already lexicographic, and base64url values need no escaping.
export const jwkThumbprint = async (jwk: Record<string, string>): Promise<string> => {
  const canonical = JSON.stringify({ crv: jwk.crv, kty: jwk.kty, x: jwk.x, y: jwk.y })
  return base64Url(await crypto.subtle.digest('SHA-256', utf8(canonical)))
}

export const publicKeyThumbprint = async (publicKey: CryptoKey): Promise<string> =>
  jwkThumbprint(await publicJwk(publicKey))

export interface ProofInput {
  method: string
  // Path only (CloudFront and the ALB rewrite scheme and host); hodor compares its request path.
  path: string
  // Omitted at exchange: hodor mints the bearer in that call, so there is nothing to bind yet.
  bearer?: string
}

// Fresh jti and iat per call: proofs are single-use and short-lived.
export const buildDpopProof = async (
  keyPair: CryptoKeyPair,
  { method, path, bearer }: ProofInput,
): Promise<string> => {
  const header = {
    alg: 'ES256',
    typ: 'dpop+jwt',
    jwk: await publicJwk(keyPair.publicKey),
  }
  const payload: Record<string, string | number> = {
    htm: method.toUpperCase(),
    htu: path,
    iat: Math.floor(Date.now() / 1000),
    jti: crypto.randomUUID(),
  }
  if (bearer) {
    payload.ath = await accessTokenHash(bearer)
  }
  const signingInput = `${encodeJson(header)}.${encodeJson(payload)}`
  const signature = await crypto.subtle.sign(SIGN_ALGORITHM, keyPair.privateKey, utf8(signingInput))
  return `${signingInput}.${base64Url(signature)}`
}

// The request path for htu, from an absolute URL or a bare path; the query never counts.
export const dpopPath = (url: string): string => {
  try {
    return new URL(url).pathname
  } catch {
    return url.split('?')[0]
  }
}
